/**
 * Goods receipt — `/purchasing/purchase-orders/:id/receive`.
 *
 * Scanner-first floor screen: an always-live scan field, large touch targets,
 * and no pointer-required action. Each scan resolves the barcode to a variant,
 * matches an open PO line and adds one unit; a wrong scan gets a strong visual
 * plus audible reject cue. Corrections are new entries, never a local undo.
 *
 * `POST /api/v1/grns` creates AND posts the receipt in one server transaction
 * (Idempotency-Key, no If-Match). Over-receipt is warned about client-side
 * from the line's snapshotted tolerance, but the server's 422 is the
 * authority and lands on the exact line.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Minus, Plus, ScanBarcode } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { formatQuantity } from "@/lib/format";
import { useIdempotentMutation } from "@/features/shared/ops-api";

// Shared scanner utility — imported RELATIVELY per the workspace contract.
import { useScannerInput } from "../../shared/scanner/src/react";

import {
  createGrn,
  lookupVariantsByBarcode,
  RECEIVABLE_STATUSES,
  type GrnCreate,
  type GrnLineInput,
} from "../api";
import {
  purchasingKeys,
  useBarcodeIndex,
  useBatchOptions,
  usePurchaseOrderDetail,
  usePurchasingConfig,
  useWarehouseLocationOptions,
  useWarehouseOptions,
} from "../queries";
import { useVariantSkus } from "../components/lookup-names";
import { DecimalInput } from "../components/decimal-input";
import { handleProblem, type FieldErrors } from "../problem-errors";
import {
  QTY_SCALE,
  addDecimals,
  compareDecimal,
  isZeroDecimal,
  receivableQty,
  remainingQty,
  subtractDecimal,
  toScale,
} from "../decimal";

const ONE_QTY = "1.000000";
const ZERO_QTY = "0.000000";

interface ReceiptLine {
  purchase_order_line_id: string;
  variant_id: string;
  ordered: string;
  received: string;
  tolerance_pct: string;
  unit_cost: string;
  /** Entered on this receipt. */
  qty_received: string;
  qty_rejected: string;
  warehouse_location_id: string;
  batch_id: string;
  batch_number: string;
}

/** Short reject cue; silently a no-op where Web Audio is unavailable. */
function playRejectCue(): void {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor === undefined) return;
    const context = new Ctor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "square";
    oscillator.frequency.value = 180;
    gain.gain.value = 0.08;
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.18);
  } catch {
    // An audio cue is an enhancement; never let it break receiving.
  }
}

function nowLocalInput(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 16);
}

export interface PoReceivePageProps {
  purchaseOrderId: string;
  canPost?: boolean;
  canReadVariants?: boolean;
  canReadBarcodes?: boolean;
  canReadWarehouses?: boolean;
  canReadBatches?: boolean;
  canReadConfig?: boolean;
  onPosted: (grnId: string) => void;
  onBack: () => void;
}

export function PoReceivePage({
  purchaseOrderId,
  canPost = false,
  canReadVariants = true,
  canReadBarcodes = true,
  canReadWarehouses = true,
  canReadBatches = true,
  canReadConfig = true,
  onPosted,
  onBack,
}: PoReceivePageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const detail = usePurchaseOrderDetail(purchaseOrderId);
  const order = detail.data?.data;
  const config = usePurchasingConfig(canReadConfig);
  const warehousesQuery = useWarehouseOptions(canReadWarehouses);
  const warehouses = useMemo(() => warehousesQuery.data ?? [], [warehousesQuery.data]);
  const { variantSku } = useVariantSkus(canReadVariants);

  const [warehouseId, setWarehouseId] = useState("");
  const [occurredAt, setOccurredAt] = useState(nowLocalInput());
  const [deliveryReference, setDeliveryReference] = useState("");
  const [lines, setLines] = useState<ReceiptLine[]>([]);
  const [scanValue, setScanValue] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const scanRef = useRef<HTMLInputElement | null>(null);

  // Scan resolution index for this order's variants (see resolveScan).
  const barcodeIndex = useBarcodeIndex(
    order?.lines.map((line) => line.variant_id) ?? [],
    canReadBarcodes,
  );

  const locationsQuery = useWarehouseLocationOptions(
    warehouseId === "" ? undefined : warehouseId,
    canReadWarehouses,
  );
  const locations = useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);

  useEffect(() => {
    if (warehouseId === "" && warehouses.length > 0) setWarehouseId(warehouses[0]?.id ?? "");
  }, [warehouseId, warehouses]);

  useEffect(() => {
    if (order === undefined) return;
    setLines(
      order.lines.map((line) => ({
        purchase_order_line_id: line.id,
        variant_id: line.variant_id,
        ordered: line.qty_ordered,
        received: line.qty_received,
        tolerance_pct:
          line.over_receipt_tolerance_pct ?? config.data?.over_receipt_tolerance_pct ?? "0.000000",
        unit_cost: line.unit_cost,
        qty_received: "",
        qty_rejected: "",
        warehouse_location_id: "",
        batch_id: "",
        batch_number: "",
      })),
    );
  }, [order, config.data?.over_receipt_tolerance_pct]);

  // Default every line to the warehouse's default receiving location.
  const defaultLocationId =
    warehouses.find((warehouse) => warehouse.id === warehouseId)?.default_location_id ?? "";
  useEffect(() => {
    if (defaultLocationId === "") return;
    setLines((current) =>
      current.map((line) =>
        line.warehouse_location_id === ""
          ? { ...line, warehouse_location_id: defaultLocationId }
          : line,
      ),
    );
  }, [defaultLocationId]);

  const updateLine = (poLineId: string, patch: Partial<ReceiptLine>): void => {
    setLines((current) =>
      current.map((line) =>
        line.purchase_order_line_id === poLineId ? { ...line, ...patch } : line,
      ),
    );
  };

  const addOne = (poLineId: string, amount: string): void => {
    setLines((current) =>
      current.map((line) => {
        if (line.purchase_order_line_id !== poLineId) return line;
        const next = addDecimals(
          [line.qty_received === "" ? ZERO_QTY : line.qty_received, amount],
          QTY_SCALE,
        );
        return {
          ...line,
          qty_received: compareDecimal(next, ZERO_QTY, QTY_SCALE) <= 0 ? "" : next,
        };
      }),
    );
  };

  /**
   * Resolve a scan against this order's own barcode index first — that is one
   * local lookup instead of a round-trip, and it keeps working even though the
   * deployed catalog service refuses the contract's `?barcode=` filter. The
   * server filter is still consulted for a code that is NOT on this order, so
   * "unknown barcode" stays distinguishable from "not on this order".
   */
  const resolveScan = async (raw: string): Promise<void> => {
    const barcode = raw.trim();
    if (barcode === "") return;
    setScanValue("");
    setScanError(null);

    const indexedVariantId = barcodeIndex.data?.[barcode];
    if (indexedVariantId !== undefined) {
      const line = lines.find((candidate) => candidate.variant_id === indexedVariantId);
      if (line !== undefined) {
        addOne(line.purchase_order_line_id, ONE_QTY);
        return;
      }
    }

    let variant: { id: string; sku: string } | undefined;
    try {
      variant = (await lookupVariantsByBarcode(barcode))[0];
    } catch {
      // The published filter is unavailable; the local index is authoritative.
      variant = undefined;
    }
    if (variant === undefined) {
      playRejectCue();
      setScanError(
        t("purchasing.receive.barcodeUnknown", "No variant for barcode {{barcode}}", {
          barcode,
        }),
      );
      return;
    }
    const line = lines.find((candidate) => candidate.variant_id === variant.id);
    if (line === undefined) {
      playRejectCue();
      setScanError(
        t("purchasing.receive.variantNotOnPo", "{{sku}} is not on this purchase order.", {
          sku: variant.sku,
        }),
      );
      return;
    }
    addOne(line.purchase_order_line_id, ONE_QTY);
  };

  useScannerInput({
    onScan: (scan) => {
      void resolveScan(scan.value);
    },
  });

  const enteredLines = lines.filter(
    (line) => line.qty_received !== "" && !isZeroDecimal(line.qty_received, QTY_SCALE),
  );

  const acceptedOf = (line: ReceiptLine): string =>
    subtractDecimal(
      line.qty_received === "" ? ZERO_QTY : line.qty_received,
      line.qty_rejected === "" ? ZERO_QTY : line.qty_rejected,
      QTY_SCALE,
    );

  const postMutation = useIdempotentMutation<Awaited<ReturnType<typeof createGrn>>, GrnCreate>({
    mutationFn: (input, key) => createGrn(input, key),
    onSuccess: (result) => {
      setFieldErrors({});
      void queryClient.invalidateQueries({ queryKey: purchasingKeys.grns.all });
      void queryClient.invalidateQueries({ queryKey: purchasingKeys.purchaseOrders.all });
      toast({
        title: t("purchasing.receive.posted", "Goods receipt {{number}} posted.", {
          number: result.grn.grn_number,
        }),
      });
      onPosted(result.grn.id);
    },
    onError: (error) => {
      // Entered quantities survive a failure so the same key can be retried.
      handleProblem(error, t, { onFieldErrors: setFieldErrors });
    },
  });

  const post = (): void => {
    setFieldErrors({});
    if (warehouseId === "") {
      setFieldErrors({
        warehouse_id: t("purchasing.receive.warehouseRequired", "Choose a receiving warehouse."),
      });
      return;
    }
    const grnLines: GrnLineInput[] = [];
    const errors: FieldErrors = {};
    enteredLines.forEach((line, index) => {
      const accepted = acceptedOf(line);
      if (compareDecimal(accepted, ZERO_QTY, QTY_SCALE) <= 0) {
        errors[`lines[${index}].qty_accepted`] = t(
          "purchasing.receive.acceptedRequired",
          "Accepted quantity must be above zero — reduce the rejected quantity.",
        );
        return;
      }
      if (line.warehouse_location_id === "") {
        errors[`lines[${index}].warehouse_location_id`] = t(
          "purchasing.receive.locationRequired",
          "Choose a location for this line.",
        );
        return;
      }
      grnLines.push({
        line_number: index + 1,
        purchase_order_line_id: line.purchase_order_line_id,
        warehouse_location_id: line.warehouse_location_id,
        qty_received: toScale(line.qty_received, QTY_SCALE),
        qty_accepted: accepted,
        qty_rejected: toScale(line.qty_rejected === "" ? ZERO_QTY : line.qty_rejected, QTY_SCALE),
        ...(line.batch_id === "" || line.batch_number === ""
          ? {}
          : { batch_id: line.batch_id, batch_number: line.batch_number }),
      });
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    if (grnLines.length === 0) {
      setFieldErrors({
        lines: t(
          "purchasing.receive.nothingEntered",
          "Enter a received quantity on at least one line.",
        ),
      });
      return;
    }
    postMutation.mutate({
      purchase_order_id: purchaseOrderId,
      warehouse_id: warehouseId,
      occurred_at: new Date(occurredAt).toISOString(),
      ...(deliveryReference === "" ? {} : { supplier_delivery_reference_token: deliveryReference }),
      lines: grnLines,
    });
  };

  const renderQty = (value: string): string => {
    try {
      return formatQuantity(value, i18n.language, QTY_SCALE);
    } catch {
      return value;
    }
  };

  if (detail.isLoading) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        {t("purchasing.common.loading", "Loading…")}
      </p>
    );
  }
  if (detail.isError || order === undefined) {
    return (
      <div className="flex flex-col items-start gap-3 p-4">
        <p role="alert" className="text-sm text-destructive">
          {t("purchasing.receive.loadError", "This purchase order could not be loaded.")}
        </p>
        <Button type="button" variant="outline" onClick={() => void detail.refetch()}>
          {t("actions.retry")}
        </Button>
      </div>
    );
  }

  if (!RECEIVABLE_STATUSES.includes(order.status)) {
    return (
      <div className="flex flex-col items-start gap-3 p-4">
        <h1 className="text-xl font-semibold">{t("purchasing.receive.title", "Receive goods")}</h1>
        <p role="status" className="rounded-md border bg-muted/40 p-3 text-sm">
          {t(
            "purchasing.receive.notReceivable",
            "This purchase order is not open for receiving in its current status.",
          )}
        </p>
        <Button type="button" variant="outline" onClick={onBack}>
          {t("purchasing.receive.backToOrder", "Back to the purchase order")}
        </Button>
      </div>
    );
  }

  const outstanding = lines.filter(
    (line) => compareDecimal(remainingQty(line.ordered, line.received), ZERO_QTY, QTY_SCALE) > 0,
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            {t("purchasing.receive.title", "Receive goods")}
          </h1>
          <p className="text-sm text-muted-foreground">
            <span dir="ltr" className="font-mono">
              {order.po_number}
            </span>
          </p>
        </div>
        <Button type="button" variant="outline" onClick={onBack}>
          {t("purchasing.receive.backToOrder", "Back to the purchase order")}
        </Button>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-3 p-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-receive-scan" className="text-base">
              {t("purchasing.receive.scanLabel", "Scan barcode")}
            </Label>
            <div className="flex items-center gap-2">
              <ScanBarcode className="size-6 shrink-0" aria-hidden="true" />
              <Input
                id="purchasing-receive-scan"
                ref={scanRef}
                // physical-ok: barcodes are Latin codes, always LTR.
                dir="ltr"
                autoFocus
                autoComplete="off"
                className="h-12 text-lg"
                value={scanValue}
                onChange={(event) => setScanValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void resolveScan(scanValue);
                  }
                }}
              />
            </div>
            {scanError !== null ? (
              <p
                role="alert"
                data-testid="purchasing-scan-error"
                className="rounded-md border border-destructive bg-destructive/10 p-2 text-sm font-medium text-destructive"
              >
                {scanError}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-receive-warehouse">
                {t("purchasing.receive.warehouse", "Warehouse")}
              </Label>
              <Select value={warehouseId} onValueChange={setWarehouseId}>
                <SelectTrigger id="purchasing-receive-warehouse">
                  <SelectValue placeholder={t("purchasing.receive.pickWarehouse", "Choose…")} />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((warehouse) => (
                    <SelectItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.warehouse_id !== undefined ? (
                <p role="alert" className="text-xs text-destructive">
                  {fieldErrors.warehouse_id}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-receive-occurred">
                {t("purchasing.receive.occurredAt", "Received at")}
              </Label>
              <Input
                id="purchasing-receive-occurred"
                type="datetime-local"
                dir="ltr"
                value={occurredAt}
                onChange={(event) => setOccurredAt(event.target.value)}
                aria-invalid={fieldErrors.occurred_at !== undefined}
              />
              {fieldErrors.occurred_at !== undefined ? (
                <p role="alert" className="text-xs text-destructive">
                  {fieldErrors.occurred_at}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="purchasing-receive-reference">
                {t("purchasing.receive.deliveryReference", "Delivery note reference")}
              </Label>
              <Input
                id="purchasing-receive-reference"
                dir="ltr"
                value={deliveryReference}
                onChange={(event) => setDeliveryReference(event.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {outstanding.length === 0 ? (
        <p className="rounded-md border bg-muted/40 p-3 text-sm">
          {t("purchasing.receive.nothingLeft", "Nothing left to receive on this order.")}
        </p>
      ) : null}

      {fieldErrors.lines !== undefined ? (
        <p role="alert" className="text-sm text-destructive">
          {fieldErrors.lines}
        </p>
      ) : null}

      <ul className="flex flex-col gap-3">
        {lines.map((line) => {
          const enteredIndex = enteredLines.findIndex(
            (candidate) => candidate.purchase_order_line_id === line.purchase_order_line_id,
          );
          const prefix = enteredIndex >= 0 ? `lines[${enteredIndex}]` : null;
          const remaining = remainingQty(line.ordered, line.received);
          const receivable = receivableQty(line.ordered, line.received, line.tolerance_pct);
          const overReceipt =
            line.qty_received !== "" &&
            compareDecimal(line.qty_received, receivable, QTY_SCALE) > 0;
          const complete = isZeroDecimal(remaining, QTY_SCALE);
          return (
            <li
              key={line.purchase_order_line_id}
              className="rounded-md border p-3"
              data-testid={`receive-line-${line.purchase_order_line_id}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span dir="ltr" className="font-mono font-medium">
                  {variantSku(line.variant_id)}
                </span>
                <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline">
                    {t("purchasing.receive.ordered", "Ordered")}{" "}
                    <span dir="ltr">{renderQty(line.ordered)}</span>
                  </Badge>
                  <Badge variant="outline">
                    {t("purchasing.receive.received", "Received")}{" "}
                    <span dir="ltr">{renderQty(line.received)}</span>
                  </Badge>
                  {complete ? (
                    <Badge variant="success">
                      {t("purchasing.receive.lineComplete", "Line complete")}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      {t("purchasing.receive.remaining", "Remaining")}{" "}
                      <span dir="ltr">{renderQty(remaining)}</span>
                    </Badge>
                  )}
                  <MoneyDisplay value={{ amount: line.unit_cost, currency: order.currency }} />
                </span>
              </div>

              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div className="flex items-end gap-2">
                  {/* Logical order keeps [-] at the inline-start in both directions. */}
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="size-12"
                    aria-label={t("purchasing.receive.decrease", "Decrease {{sku}}", {
                      sku: variantSku(line.variant_id),
                    })}
                    onClick={() => addOne(line.purchase_order_line_id, `-${ONE_QTY}`)}
                  >
                    <Minus className="size-5" aria-hidden="true" />
                  </Button>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`receive-qty-${line.purchase_order_line_id}`}>
                      {t("purchasing.receive.thisReceipt", "This receipt")}
                    </Label>
                    <DecimalInput
                      id={`receive-qty-${line.purchase_order_line_id}`}
                      className="h-12 w-32 text-center text-lg"
                      scale={QTY_SCALE}
                      value={line.qty_received}
                      onChange={(next) =>
                        updateLine(line.purchase_order_line_id, { qty_received: next })
                      }
                      aria-invalid={
                        prefix !== null && fieldErrors[`${prefix}.qty_received`] !== undefined
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="size-12"
                    aria-label={t("purchasing.receive.increase", "Increase {{sku}}", {
                      sku: variantSku(line.variant_id),
                    })}
                    onClick={() => addOne(line.purchase_order_line_id, ONE_QTY)}
                  >
                    <Plus className="size-5" aria-hidden="true" />
                  </Button>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`receive-rejected-${line.purchase_order_line_id}`}>
                    {t("purchasing.receive.rejected", "Rejected")}
                  </Label>
                  <DecimalInput
                    id={`receive-rejected-${line.purchase_order_line_id}`}
                    className="h-12 w-28 text-center"
                    scale={QTY_SCALE}
                    value={line.qty_rejected}
                    onChange={(next) =>
                      updateLine(line.purchase_order_line_id, { qty_rejected: next })
                    }
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`receive-location-${line.purchase_order_line_id}`}>
                    {t("purchasing.receive.location", "Location")}
                  </Label>
                  <Select
                    value={line.warehouse_location_id}
                    onValueChange={(value) =>
                      updateLine(line.purchase_order_line_id, { warehouse_location_id: value })
                    }
                  >
                    <SelectTrigger
                      id={`receive-location-${line.purchase_order_line_id}`}
                      className="h-12 w-56"
                    >
                      <SelectValue placeholder={t("purchasing.receive.pickLocation", "Choose…")} />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <BatchPicker
                  line={line}
                  warehouseId={warehouseId}
                  enabled={canReadBatches}
                  onPick={(batchId, batchNumber) =>
                    updateLine(line.purchase_order_line_id, {
                      batch_id: batchId,
                      batch_number: batchNumber,
                    })
                  }
                />
              </div>

              {overReceipt ? (
                <p role="status" className="mt-2 text-sm text-warning-foreground">
                  {t(
                    "purchasing.receive.overReceiptWarning",
                    "Above the allowed remaining quantity of {{allowed}} (tolerance {{pct}}%). The server may refuse this line.",
                    { allowed: renderQty(receivable), pct: line.tolerance_pct },
                  )}
                </p>
              ) : null}
              {prefix !== null && fieldErrors[`${prefix}.qty_received`] !== undefined ? (
                <p role="alert" className="mt-2 text-sm text-destructive">
                  {fieldErrors[`${prefix}.qty_received`]}
                </p>
              ) : null}
              {prefix !== null && fieldErrors[`${prefix}.qty_accepted`] !== undefined ? (
                <p role="alert" className="mt-2 text-sm text-destructive">
                  {fieldErrors[`${prefix}.qty_accepted`]}
                </p>
              ) : null}
              {prefix !== null && fieldErrors[`${prefix}.warehouse_location_id`] !== undefined ? (
                <p role="alert" className="mt-2 text-sm text-destructive">
                  {fieldErrors[`${prefix}.warehouse_location_id`]}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>

      <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t bg-background/95 py-3">
        <span className="text-sm text-muted-foreground">
          {t("purchasing.receive.enteredLines", "{{count}} line(s) entered", {
            count: enteredLines.length,
          })}
        </span>
        {canPost ? (
          <Button
            type="button"
            className="h-12 px-8 text-base"
            loading={postMutation.isPending}
            disabled={postMutation.isPending || enteredLines.length === 0}
            onClick={post}
          >
            {t("purchasing.receive.post", "Post goods receipt")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function BatchPicker({
  line,
  warehouseId,
  enabled,
  onPick,
}: {
  line: ReceiptLine;
  warehouseId: string;
  enabled: boolean;
  onPick: (batchId: string, batchNumber: string) => void;
}) {
  const { t } = useTranslation();
  const batches = useBatchOptions(
    line.variant_id,
    warehouseId === "" ? undefined : warehouseId,
    enabled,
  );
  const rows = batches.data ?? [];
  if (!enabled || rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`receive-batch-${line.purchase_order_line_id}`}>
        {t("purchasing.receive.batch", "Batch")}
      </Label>
      <Select
        value={line.batch_id}
        onValueChange={(value) => {
          const batch = rows.find((candidate) => candidate.id === value);
          onPick(value, batch?.batch_no ?? "");
        }}
      >
        <SelectTrigger id={`receive-batch-${line.purchase_order_line_id}`} className="h-12 w-44">
          <SelectValue placeholder={t("purchasing.receive.pickBatch", "Choose…")} />
        </SelectTrigger>
        <SelectContent>
          {rows.map((batch) => (
            <SelectItem key={batch.id} value={batch.id}>
              {batch.batch_no}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
